import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import type { PricingTier } from "@/app/marketing/constants/pricing";
import { cn } from "@/lib/utils";

interface PricingCardProps {
  tier: PricingTier;
}

export function PricingCard({ tier }: PricingCardProps) {
  const internalCta = tier.ctaHref.startsWith("/");
  return (
    <div
      className={cn(
        "relative rounded-2xl border p-6 md:p-8 flex flex-col",
        tier.highlight
          ? "border-brand-violet bg-gradient-to-b from-brand-violet/10 to-transparent shadow-2xl shadow-brand-violet/20"
          : "border-white/10 bg-white/5",
      )}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-6 rounded-full bg-brand-violet px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
          Most popular
        </span>
      )}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-ink/60">
          {tier.name}
        </h3>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-display text-5xl text-brand-ink">{tier.price}</span>
          {tier.priceCadence && (
            <span className="text-sm text-brand-ink/60">{tier.priceCadence}</span>
          )}
        </div>
        <p className="mt-3 text-sm text-brand-ink/70">{tier.blurb}</p>
      </div>

      <ul className="mt-6 space-y-3 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="h-4 w-4 text-brand-violet shrink-0 mt-0.5" />
            <span className="text-sm text-brand-ink/80">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        asChild
        variant={tier.highlight ? "primary" : "outline"}
        className={cn(
          "mt-8 w-full",
          !tier.highlight && "border-white/20 text-brand-ink hover:bg-white/5",
        )}
      >
        {internalCta ? (
          <Link to={tier.ctaHref}>{tier.ctaLabel}</Link>
        ) : (
          <a href={tier.ctaHref}>{tier.ctaLabel}</a>
        )}
      </Button>
    </div>
  );
}
